<template>
  <ib-dialog v-model="internalDialog" max-width="800">
    <v-card v-if="opportunity">
      <ib-dialog-header :title="title" :subtitle="subtitle" :close="closeDialog" />
      
      <v-card-text class="pb-0">
        <v-row>
          <v-col cols="12">
            <ib-text-field :value="opportunity.title" @input="updateField('title', $event)" label="Título" outlined hide-details="auto" />
          </v-col>

          <v-col cols="12">
            <v-textarea :value="opportunity.description" @input="updateField('description', $event)" class="nd-text-field" label="Descrição" outlined rows="4" hide-details="auto" />
          </v-col>

          <v-col v-if="showContactField" cols="12" md="6">
            <ib-autocomplete :value="opportunity.contact" @input="updateField('contact', $event)" return-object outlined :items="contacts" :loading="loadingContacts" clearable hide-details="auto" :item-text="getContactItemText" item-value="_id" label="Contato vinculado" placeholder="Busque por nome ou telefone" no-filter @update:search-input="handleContactSearch">
              <template #no-data>
                <v-list-item>
                  <v-list-item-title>
                    Busque um contato pelo
                    <strong>nome ou telefone</strong>
                  </v-list-item-title>
                </v-list-item>
              </template>
              <template #selection="{ item }">
                <div class="d-flex align-center">
                  <ib-user-avatar :user="item" size="24" class="mr-2" />
                  <span>{{ item.override_name || item.name }}</span>
                </div>
              </template>
              <template #item="{ item }">
                <div class="d-flex align-center">
                  <ib-user-avatar :user="item" size="24" class="mr-2" />
                  <v-list-item-content>
                    <v-list-item-title v-text="item.override_name || item.name" />
                    <v-list-item-subtitle v-text="item.phone" />
                  </v-list-item-content>
                </div>
              </template>
            </ib-autocomplete>
          </v-col>

          <v-col cols="12" :md="showContactField ? 6 : 6">
            <ib-select :value="opportunity.assigned_user" @input="updateField('assigned_user', $event)" :items="usersList" item-text="name" item-value="_id" label="Responsável" :clearable="showContactField" outlined hide-details="auto">
              <template #selection="{ item }">
                <div class="d-flex align-center">
                  <ib-user-avatar :user="item" size="24" class="mr-2" />
                  <span>{{ item.name }}</span>
                </div>
              </template>
              <template #item="{ item }">
                <div class="d-flex align-center">
                  <ib-user-avatar :user="item" size="24" class="mr-2" />
                  <span>{{ item.name }}</span>
                </div>
              </template>
            </ib-select>
          </v-col>

          <v-col cols="12" md="6">
            <ib-text-field :value="opportunity.value" @input="updateField('value', $event)" v-money="money" label="Valor" outlined hide-details="auto" />
          </v-col>

          <v-col v-if="showFunnelField" cols="12" md="6">
            <ib-select :value="opportunity.funnel" @input="updateField('funnel', $event)" :items="funnels" item-text="name" item-value="_id" label="Funil" outlined hide-details="auto" @change="handleFunnelChange" />
          </v-col>

          <v-col v-if="steps.length > 0" cols="12" md="6">
            <ib-select :value="stepValue" @input="updateStepField" :items="steps" item-text="title" item-value="_id" label="Etapa" outlined hide-details="auto" :disabled="isOpportunityClosed">
              <template #selection="{ item }">
                <v-icon small :color="item.color" class="mr-2">
                  mdi-circle
                </v-icon>
                {{ item.title }}
              </template>
              <template #item="{ item }">
                <v-icon small :color="item.color" class="mr-2">
                  mdi-circle
                </v-icon>
                {{ item.title }}
              </template>
            </ib-select>
          </v-col>
        </v-row>

        <!-- Status Badge (quando oportunidade já está ganha/perdida) -->
        <div v-if="isOpportunityClosed" class="status-badge-section mt-4">
          <v-alert :type="opportunity.status === 'won' ? 'success' : 'error'" :icon="opportunity.status === 'won' ? 'mdi-check-circle' : 'mdi-close-circle'" dense outlined class="mb-2">
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong>{{ opportunity.status === 'won' ? 'Oportunidade Ganha' : 'Oportunidade Perdida' }}</strong>
                <div v-if="opportunity.closed_at" class="text-caption mt-1">
                  Fechada em {{ formatDate(opportunity.closed_at) }}
                </div>
                <div v-if="opportunity.status === 'won' && opportunity.final_value" class="text-caption">
                  Valor final: {{ formatCurrencyValue(opportunity.final_value) }}
                </div>
                <div v-if="opportunity.status === 'lost' && opportunity.loss_reason" class="text-caption">
                  Motivo: {{ opportunity.loss_reason }}
                </div>
              </div>
              <ib-btn small outlined color="primary" :loading="statusLoading" @click="handleReopen">
                <v-icon small left>mdi-refresh</v-icon>
                Reabrir
              </ib-btn>
            </div>
          </v-alert>
        </div>

        <!-- Ações de status (apenas quando editando oportunidade aberta) -->
        <div v-if="isEditing && !isOpportunityClosed" class="status-actions-section mt-4">
          <v-divider class="mb-4" />
          <p class="text-subtitle-2 grey--text text--darken-2 mb-3">Resultado da Oportunidade</p>

          <div v-if="!statusAction" class="d-flex" style="gap: 0.75rem;">
            <ib-btn color="primary" outlined @click="statusAction = 'won'">
              <v-icon left>mdi-check-circle-outline</v-icon>
              Marcar como Ganha
            </ib-btn>
            <ib-btn color="error" outlined @click="statusAction = 'lost'">
              <v-icon left>mdi-close-circle-outline</v-icon>
              Marcar como Perdida
            </ib-btn>
          </div>

          <!-- Sub-formulário: Ganha -->
          <div v-if="statusAction === 'won'" class="status-sub-form pa-3 rounded-lg">
            <p class="text-subtitle-2 primary--text mb-3">
              <v-icon small color="primary" class="mr-1">mdi-check-circle</v-icon>
              Marcar como Ganha
            </p>
            <ib-text-field v-model="statusFinalValue" v-money="money" label="Valor final (opcional)" outlined hide-details="auto" class="mb-3" />
            <div class="d-flex" style="gap: 0.5rem;">
              <ib-btn small color="gray200" outlined @click="statusAction = null; statusFinalValue = '';">
                <div class="gray1000--text">Cancelar</div>
              </ib-btn>
              <ib-btn small color="primary" :loading="statusLoading" @click="confirmWon">
                <v-icon small left>mdi-check</v-icon>
                Confirmar
              </ib-btn>
            </div>
          </div>

          <!-- Sub-formulário: Perdida -->
          <div v-if="statusAction === 'lost'" class="status-sub-form pa-3 rounded-lg">
            <p class="text-subtitle-2 error--text mb-3">
              <v-icon small color="error" class="mr-1">mdi-close-circle</v-icon>
              Marcar como Perdida
            </p>
            <ib-select v-model="statusLossReason" :items="lossReasonOptions" label="Motivo da perda" outlined hide-details="auto" class="mb-3" :rules="[v => !!v || 'Motivo obrigatório']" />
            <ib-text-field v-if="statusLossReason === 'Outro'" v-model="customLossReason" label="Motivo personalizado" outlined hide-details="auto" class="mb-3" :rules="[v => !!(v && v.trim()) || 'Motivo obrigatório']" />
            <div class="d-flex" style="gap: 0.5rem;">
              <ib-btn small color="gray200" outlined @click="statusAction = null; statusLossReason = ''; customLossReason = '';">
                <div class="gray1000--text">Cancelar</div>
              </ib-btn>
              <ib-btn small color="error" :loading="statusLoading" :disabled="!resolvedLossReason" @click="confirmLost">
                <v-icon small left>mdi-check</v-icon>
                Confirmar
              </ib-btn>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="mt-4">
        <ib-btn color="gray200" outlined @click="closeDialog">
          <div class="gray1000--text">
            Cancelar
          </div>
        </ib-btn>
        <v-spacer />
        <ib-btn v-if="!isOpportunityClosed" color="primary" :loading="saving" :disabled="saveDisabled" @click="handleSave">
          {{ saveButtonText }}
        </ib-btn>
      </v-card-actions>
    </v-card>
  </ib-dialog>
</template>

<script>
export default {
  name: "DialogCreateOpportunity",

  props: {
    value: {
      type: Boolean,
      default: false
    },
    opportunity: {
      type: Object,
      default: null
    },
    funnels: {
      type: Array,
      default: () => []
    },
    steps: {
      type: Array,
      default: () => []
    },
    usersList: {
      type: Array,
      default: () => []
    },
    contacts: {
      type: Array,
      default: () => []
    },
    loadingContacts: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Criar Oportunidade'
    },
    subtitle: {
      type: String,
      default: ''
    },
    saveButtonText: {
      type: String,
      default: 'Criar oportunidade'
    },
    showContactField: {
      type: Boolean,
      default: false
    },
    showFunnelField: {
      type: Boolean,
      default: true
    },
    stepValue: {
      type: String,
      default: null
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    statusLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      money: { decimal: ',', thousands: '.', prefix: 'R$ ', precision: 2, masked: false },
      statusAction: null,
      statusFinalValue: '',
      statusLossReason: '',
      customLossReason: '',
      lossReasonOptions: ['Preço', 'Sem resposta', 'Escolheu concorrente', 'Fora do perfil', 'Outro']
    };
  },

  computed: {
    internalDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    saveDisabled() {
      if (this.showFunnelField) {
        return !this.opportunity.funnel || !this.stepValue;
      }
      return !this.stepValue;
    },

    isOpportunityClosed() {
      return this.opportunity && (this.opportunity.status === 'won' || this.opportunity.status === 'lost');
    },

    resolvedLossReason() {
      if (this.statusLossReason === 'Outro') return this.customLossReason.trim();
      return this.statusLossReason;
    }
  },

  watch: {
    value(val) {
      if (!val) {
        this.statusAction = null;
        this.statusFinalValue = '';
        this.statusLossReason = '';
        this.customLossReason = '';
      }
    }
  },

  methods: {
    closeDialog() {
      this.$emit('input', false);
    },

    updateField(field, value) {
      this.$emit('update-field', { field, value });
    },

    updateStepField(value) {
      this.$emit('step-change', value);
    },

    getContactItemText(item) {
      return item.override_name || item.name;
    },

    handleContactSearch(value) {
      this.$emit('contact-search', value);
    },

    handleFunnelChange() {
      this.$emit('funnel-change');
    },

    handleSave() {
      this.$emit('save');
    },

    confirmWon() {
      const rawValue = this.statusFinalValue ? parseFloat(this.statusFinalValue.replace(/[R$\s.]/g, '').replace(',', '.')) || null : null;
      this.$emit('mark-won', { finalValue: rawValue });
    },

    confirmLost() {
      if (!this.resolvedLossReason) return;
      this.$emit('mark-lost', { lossReason: this.resolvedLossReason });
    },

    handleReopen() {
      this.$emit('reopen');
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    formatCurrencyValue(value) {
      if (value == null) return '';
      return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
  }
};
</script>

<style scoped>
.status-sub-form {
  background-color: var(--v-gray50-base, #f5f5f5);
  border: 1px solid var(--v-gray200-base, #e0e0e0);
}
</style>
